import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { VehicleTypes } from '@hero/schema';
import { BuildingIcon, UserIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/Form/Form';
import { Spinner } from '../../Spinner';
import { VehicleTypeSelector } from '../../../feature/Vehicle/VehicleTypeSelector';
import { IconInput } from './IconInput';
import { SelectableCard } from './SelectableCard';

export type DriverEmployment = 'freelancer' | 'company';

export interface DriverStepValues {
  name: string;
  driverEmployment: DriverEmployment;
  /**
   * The single vehicle type the driver operates. A driver always
   * drives one vehicle in this signup flow — fleets are an Org
   * concern, not a driver concern. The wizard wraps it in `[type]`
   * before submitting so the unified `signUpDriverSchema.vehicleTypes`
   * (an array) is satisfied.
   */
  vehicleType?: VehicleTypes;
  /** Required when `driverEmployment === 'company'`. */
  companyName?: string;
}

export interface DriverStepProps {
  onSubmit: (values: DriverStepValues) => void | Promise<void>;
  onBack: () => void;
  isPending: boolean;
}

const EMPLOYMENT_IDS: DriverEmployment[] = ['freelancer', 'company'];

const sectionMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
  transition: { duration: 0.2, ease: 'easeOut' },
} as const;

/**
 * Step 2 (Driver branch). A driver picks a single vehicle type they
 * operate. Total fleet counts are an Org-only concept (carrier /
 * broker orgs report `Org.meta.estimatedVehicleTotal`); a driver is
 * always one person with one vehicle here, so we don't ask.
 *
 * When the driver works for a company (`driverEmployment === 'company'`),
 * they also create the carrier Org they drive under.
 */
export function DriverStep({ onSubmit, onBack, isPending }: DriverStepProps) {
  const { t } = useTranslation('m-auth');

  const schema = z
    .object({
      name: z.string().trim().min(1, { message: t('form.name.validation') }),
      driverEmployment: z.enum(['freelancer', 'company']),
      vehicleType: z.string().min(1, {
        message: t('form.vehicle_type.validation'),
      }) as unknown as z.ZodType<VehicleTypes>,
      companyName: z.string().trim().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.driverEmployment === 'company' && (!data.companyName || data.companyName === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.company_name.validation'),
          path: ['companyName'],
        });
      }
    });

  const form = useForm<DriverStepValues>({
    resolver: zodResolver(schema) as never,
    mode: 'onChange',
    defaultValues: {
      name: '',
      driverEmployment: 'freelancer',
      vehicleType: undefined,
      companyName: '',
    },
  });

  const watchedEmployment = form.watch('driverEmployment');

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('driver.step_title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('driver.step_subtitle')}</p>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name.title')}</FormLabel>
              <FormControl>
                <IconInput
                  icon={<UserIcon size="small" />}
                  placeholder={t('form.name.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="driverEmployment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('driver.employment_section_title')}</FormLabel>
              <div className="space-y-3">
                {EMPLOYMENT_IDS.map(emp => (
                  <SelectableCard
                    key={emp}
                    selected={field.value === emp}
                    onClick={() => field.onChange(emp)}
                    title={t(`driver.employment.${emp}.title`)}
                    description={t(`driver.employment.${emp}.description`)}
                  />
                ))}
              </div>
            </FormItem>
          )}
        />

        <AnimatePresence initial={false}>
          {watchedEmployment === 'company' ? (
            <motion.div key="company-name" {...sectionMotion} className="overflow-hidden">
              <div className="px-1 pt-1">
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.company_name.title')}</FormLabel>
                      <FormControl>
                        <IconInput
                          icon={<BuildingIcon size="small" />}
                          placeholder={t('form.company_name.placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <FormItem>
          <FormLabel>{t('form.vehicle_type.title')}</FormLabel>
          <FormDescription>{t('form.vehicle_type.description')}</FormDescription>
          <Controller
            control={form.control}
            name="vehicleType"
            render={({ field, fieldState }) => (
              <>
                <VehicleTypeSelector
                  value={field.value}
                  onVehicleChange={field.onChange}
                  placeholder={t('form.vehicle_type.placeholder')}
                />
                {fieldState.error?.message ? (
                  <p className="text-sm font-medium text-destructive" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </>
            )}
          />
        </FormItem>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            {t('buttons.back')}
          </Button>
          <Button
            type="submit"
            disabled={isPending || !form.formState.isValid}
            className="flex-1">
            {isPending ? <Spinner /> : t('buttons.create_account')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
